import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Button, Skeleton } from '@bbpos/ui';
import { locationService } from '@/services/location.service';
import { LocationAssignment } from './LocationAssignment';
import type { UserLocation } from '@/types/user';

const PANEL_CLASS =
  'rounded-2xl border border-slate-200/80 bg-white shadow-[0_12px_30px_-18px_rgba(15,23,42,0.22)]';

function cn(...classes: Array<string | false | null | undefined>) {
  return classes.filter(Boolean).join(' ');
}

interface UserLocationsTabProps {
  currentLocations: UserLocation[];
  onSave: (locationIds: string[]) => void;
  saving?: boolean;
}

export function UserLocationsTab({
  currentLocations,
  onSave,
  saving,
}: UserLocationsTabProps) {
  const [selected, setSelected] = useState<string[]>(currentLocations.map((l) => l.id));

  const { data: allLocations, isLoading } = useQuery({
    queryKey: ['locations', 'all'],
    queryFn: () => locationService.list({ limit: 100 }),
    staleTime: 60_000,
  });

  const locations = allLocations?.data ?? [];
  const hasChanges =
    selected.length !== currentLocations.length ||
    selected.some((id) => !currentLocations.find((l) => l.id === id));

  if (isLoading) {
    return <Skeleton variant="rect" height={200} />;
  }

  return (
    <div className={cn(PANEL_CLASS)}>
      <div className="p-5 border-b border-slate-100">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="flex h-9 w-9 items-center justify-center rounded-xl bg-sky-50 text-sky-600">
              <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M15 10.5a3 3 0 11-6 0 3 3 0 016 0z" />
                <path strokeLinecap="round" strokeLinejoin="round" d="M19.5 10.5c0 7.142-7.5 11.25-7.5 11.25S4.5 17.642 4.5 10.5a7.5 7.5 0 1115 0z" />
              </svg>
            </div>
            <div>
              <h3 className="text-sm font-semibold text-slate-900">Assigned Locations</h3>
              <p className="text-xs text-slate-500">
                {selected.length} of {locations.length} locations assigned
              </p>
            </div>
          </div>
          {hasChanges && (
            <Button variant="primary" size="sm" onClick={() => onSave(selected)} disabled={saving}>
              {saving ? 'Saving...' : 'Save Locations'}
            </Button>
          )}
        </div>
      </div>
      <div className="p-5">
        <LocationAssignment
          locations={locations}
          selected={selected}
          onChange={setSelected}
        />
      </div>
    </div>
  );
}
